/**
 * DataTable — tabela responsiva pro painel admin.
 *
 * Desktop (md+): renderiza grid com colunas tipo CSS grid-template-columns,
 *                igual ao padrao manual atual.
 * Mobile (<md):  cada row vira um card empilhado com `label: value` pairs.
 *                Colunas com `mobileHidden=true` somem no mobile.
 *                Se `mobileTitle` e/ou `mobileSubtitle` foram passados, eles
 *                aparecem destacados no topo do card.
 *
 * Mantem todo o look atual (sort header, badge Banido/Ativo, custom cells,
 * hover, link) — so' adapta layout pra mobile sem precisar mexer em cada page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, Loader2 } from 'lucide-react';

export type DataTableColumn<T> = {
  /** chave unica da coluna (usada em sort, react key) */
  key: string;
  /** label do header (e tambem usado como label do par no card mobile) */
  label: string;
  /** largura CSS pro grid-template-columns. Ex: '80px', '1.5fr', '2fr', '100px' */
  width: string;
  /** alinhamento horizontal — default left */
  align?: 'left' | 'right' | 'center';
  /** chave de sort. Se ausente, header nao e' clicavel */
  sortKey?: string;
  /** custom render. Se ausente, mostra (row as any)[key] */
  cell?: (row: T) => React.ReactNode;
  /** se true, esconde no mobile (pra cols menos relevantes) */
  mobileHidden?: boolean;
};

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  /** se passado, row inteira vira Link */
  rowHref?: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: string;
  /** estado de sort atual */
  sort?: { key: string; order: 'asc' | 'desc' };
  /** click no header com sortKey */
  onSort?: (key: string) => void;
  /** titulo destacado no card mobile (rendizado em font maior no topo) */
  mobileTitle?: (row: T) => React.ReactNode;
  /** subtitulo logo abaixo do titulo no card mobile */
  mobileSubtitle?: (row: T) => React.ReactNode;
  /** linha de "actions" fixa no fim do card mobile (botoes etc) */
  mobileActions?: (row: T) => React.ReactNode;
  /** class extra no surface-card raiz */
  className?: string;
};

export function DataTable<T>({
  columns, rows, rowKey, rowHref, onRowClick,
  loading, empty,
  sort, onSort,
  mobileTitle, mobileSubtitle, mobileActions,
  className,
}: DataTableProps<T>) {
  const gridTemplate = columns.map((c) => c.width).join(' ');
  const baseGridClass = 'gap-4 px-4 md:px-5 py-3';
  const alignClass = (a?: 'left' | 'right' | 'center') =>
    a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';

  const visibleCols = columns;
  const mobileCols = columns.filter((c) => !c.mobileHidden);

  const renderRow = (row: T, index: number) => {
    const key = rowKey(row);
    const cells = visibleCols.map((col) => {
      const content = col.cell
        ? col.cell(row)
        : (row as Record<string, unknown>)[col.key] as React.ReactNode;
      return (
        <span key={col.key} className={`${alignClass(col.align)} truncate`}>
          {content ?? '—'}
        </span>
      );
    });

    /* ──────── Desktop row (md+) ──────── */
    const desktopRow = (
      <div
        className={`hidden md:grid ${baseGridClass} items-center
                    border-b border-white/[0.04] last:border-0
                    ${rowHref || onRowClick ? 'hover:bg-white/[0.02] cursor-pointer' : ''}`}
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {cells}
      </div>
    );

    /* ──────── Mobile card (<md) ──────── */
    // Actions ficam absolutas no canto top-right pra nao crescer o card numa
    // linha so' de botoes. Card tem padding-right extra quando ha actions.
    const mobileCard = (
      <div
        className={`md:hidden mx-3 my-2 first:mt-3 last:mb-3 p-3.5 ${mobileActions ? 'pr-12' : ''}
                    rounded-xl border border-white/[0.06] bg-[#0f1620]/60 relative
                    ${rowHref || onRowClick ? 'hover:bg-[#141d27]/80 cursor-pointer transition' : ''}`}
      >
        {mobileActions && (
          <div className="absolute top-2 right-2 z-10">
            {mobileActions(row)}
          </div>
        )}
        {(mobileTitle || mobileSubtitle) && (
          <div className="mb-3">
            {mobileTitle && (
              <div className="text-sm font-bold text-white">{mobileTitle(row)}</div>
            )}
            {mobileSubtitle && (
              <div className="text-[11px] text-white/55 mt-0.5">{mobileSubtitle(row)}</div>
            )}
          </div>
        )}
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
          {mobileCols.map((col) => {
            const content = col.cell
              ? col.cell(row)
              : (row as Record<string, unknown>)[col.key] as React.ReactNode;
            return (
              <React.Fragment key={col.key}>
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/45 self-center">
                  {col.label}
                </span>
                <span className="text-xs text-white/85 min-w-0 break-words">
                  {content ?? '—'}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );

    const inner = (
      <>
        {desktopRow}
        {mobileCard}
      </>
    );

    if (rowHref) {
      return (
        <Link key={key} to={rowHref(row)} className="block">
          {inner}
        </Link>
      );
    }
    if (onRowClick) {
      return (
        <div key={key} onClick={() => onRowClick(row)}>
          {inner}
        </div>
      );
    }
    return <div key={key}>{inner}</div>;
  };

  return (
    <div className={`surface-card rounded-xl overflow-hidden ${className ?? ''}`}>
      {/* Header — so' desktop */}
      <div
        className={`hidden md:grid ${baseGridClass} border-b border-white/[0.06]
                    text-[10px] font-bold uppercase tracking-wider text-white/55`}
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {visibleCols.map((col) => {
          const sortable = !!col.sortKey && !!onSort;
          const isSorted = sortable && sort?.key === col.sortKey;
          return (
            <button
              key={col.key}
              onClick={() => sortable && onSort?.(col.sortKey!)}
              disabled={!sortable}
              className={`flex items-center gap-1 transition cursor-pointer ${alignClass(col.align)}
                          ${sortable ? 'hover:text-white cursor-pointer' : 'cursor-default'}`}
            >
              {col.label}
              {isSorted && (sort!.order === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading && rows.length === 0 ? (
        <div className="py-16 flex justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-white/40 text-sm">
          {empty ?? 'Nenhum item encontrado'}
        </div>
      ) : (
        rows.map(renderRow)
      )}
    </div>
  );
}
